import { Routes } from '@angular/router';
import { authGuard } from './guards/auth.guard';
import { staffGuard, adminGuard, customerGuard } from './guards/role.guard';
import { PostLoginComponent } from './components/post-login/post-login.component';
import { ServiceProviderAppComponent } from './components/service-provider-app/service-provider-app.component';
import { BarOwnerDashboardComponent } from './components/bar-owner-dashboard/bar-owner-dashboard.component';
import { StaffDashboardComponent } from './components/staff-dashboard/staff-dashboard.component';
import { PaymentInterfaceComponent } from './components/payment-interface/payment-interface.component';
import { StaffManagementComponent } from './components/staff-management/staff-management.component';
import { QrCodeGeneratorComponent } from './components/qr-code-generator/qr-code-generator.component';
import { AnalyticsReportsComponent } from './components/analytics-reports/analytics-reports.component';
import { CustomerAppComponent } from './components/customer-app/customer-app.component';
import { HomeComponent } from './components/home/home.component';
import { LoginComponent } from './components/auth/login/login.component';
import { RegisterComponent } from './components/auth/register/register.component';
import { RegisterBarComponent } from './components/auth/register-bar/register-bar.component';
import { ForgotPasswordComponent } from './components/auth/forgot-password/forgot-password.component';
import { ResetPasswordComponent } from './components/auth/reset-password/reset-password.component';
import { ChangePasswordComponent } from './components/auth/change-password/change-password.component';
import { AdminDashboardComponent } from './components/admin-dashboard/admin-dashboard.component';
import { CustomerDashboardComponent } from './components/customer-dashboard/customer-dashboard.component';
import { NotificationsComponent } from './components/notifications/notifications.component';
import { LivechatComponent } from './components/livechat/livechat.component';
import { RatingsComponent } from './components/ratings/ratings.component';
import { BarDiscoveryComponent } from './components/bar-discovery/bar-discovery.component';
import { PaymentReturnComponent } from './components/payment-return/payment-return.component';
import { VerifyEmailComponent } from './components/auth/verify-email/verify-email.component';

export const routes: Routes = [
  // ── Public routes — no authentication required ─────────────────────────────
  { path: '',              component: HomeComponent },
  { path: 'bars',          component: BarDiscoveryComponent },
  { path: 'login',         component: LoginComponent },
  { path: 'register',      component: RegisterComponent },
  // Verifying an address is not creating an account, so it is NOT the
  // register route. The email link lands here.
  { path: 'verify-email',  component: VerifyEmailComponent },
  { path: 'register-bar',  component: RegisterBarComponent },
  { path: 'forgot-password', component: ForgotPasswordComponent },
  { path: 'reset-password',  component: ResetPasswordComponent },
  { path: 'logout-success',  redirectTo: '', pathMatch: 'full' },

  // Where the provider's hosted checkout page sends the customer back to.
  // Public: the customer paying by card is anonymous — they arrived by
  // scanning a table QR code and never signed in.
  { path: 'payment/complete',  component: PaymentReturnComponent },
  { path: 'payment/cancelled', component: PaymentReturnComponent },

  // ── Authenticated routes — canActivate: [authGuard] on every protected page ─
  { path: 'change-password',  component: ChangePasswordComponent,  canActivate: [authGuard] },
  // Where a login lands. It reads the user_type claim and forwards a customer
  // to /customer and bar staff to /service-provider. This replaces the old
  // /selection screen, which asked people to declare a role the token already
  // carried — and, by offering the bar dashboard to everyone, put staff screens
  // one click from any customer.
  { path: 'post-login',       component: PostLoginComponent,       canActivate: [authGuard] },
  // /customer is the QR-scan public entry point — no auth. The anonymous
  // customer-session endpoints on customer-service permit this flow.
  { path: 'customer',         component: CustomerAppComponent },
  { path: 'customer/dashboard', component: CustomerDashboardComponent, canActivate: [customerGuard] },
  { path: 'notifications',    component: NotificationsComponent,   canActivate: [authGuard] },
  { path: 'chat',             component: LivechatComponent,        canActivate: [authGuard] },
  { path: 'livechat',         component: LivechatComponent,        canActivate: [authGuard] },
  { path: 'service-provider/chat', component: LivechatComponent,   canActivate: [staffGuard] },
  { path: 'ratings',          component: RatingsComponent,         canActivate: [authGuard] },

  // ── Service-provider (bar staff / owner) routes ───────────────────────────
  // staffGuard, not authGuard: these used to admit ANY signed-in user, so a
  // customer could open the bar's inventory, staff and takings by typing the
  // address. The guard is the browser's half only — each service still
  // authorises every call on its own.
  { path: 'service-provider',            component: ServiceProviderAppComponent, canActivate: [staffGuard] },
  { path: 'service-provider/inventory',  component: BarOwnerDashboardComponent,  canActivate: [staffGuard] },
  { path: 'service-provider/orders',     component: StaffDashboardComponent,     canActivate: [staffGuard] },
  { path: 'service-provider/payments',   component: PaymentInterfaceComponent,   canActivate: [staffGuard] },
  { path: 'service-provider/staff',      component: StaffManagementComponent,    canActivate: [staffGuard] },
  { path: 'service-provider/qr-codes',   component: QrCodeGeneratorComponent,    canActivate: [staffGuard] },
  { path: 'service-provider/analytics',  component: AnalyticsReportsComponent,   canActivate: [staffGuard] },

  // ── Admin routes ───────────────────────────────────────────────────────────
  { path: 'admin', component: AdminDashboardComponent, canActivate: [adminGuard] },

  { path: '**', redirectTo: '' }
];
